//reducers
import { selectTranslations } from 'state/reducers/translation.reducers';
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import { getBeneficiaryById } from 'state/reducers/beneficiary.reducers';
//components
import { FormsStack } from 'theme/elements/Forms';
import { InfoCard } from 'theme/elements/Cards';
import { FormHeader1 } from 'theme/elements/Headers';
//mui
import { Stack } from '@mui/system';
import { Button, useTheme } from '@mui/material';
import { FormButton } from 'theme/elements/Buttons';
import { pagesEnum, timing } from 'constants/ui.constants';
import { useNavigate, useParams } from 'react-router-dom';
import { BeneficiaryDetailsBankAccount, paymentsMethodsDisplayList } from 'constants/beneficiary.constants';
import BeneficiaryNameWithFlag from 'sections/beneficiary/details/BeneficiaryNameWithFlag';
import { Pay, PayEnum, paymentReasonList, paymentsTypes } from 'constants/pay.constants';
import { getNewPay, toggleUpserting } from 'state/reducers/pay.reducers';
import { Paragraph3 } from 'theme/elements/Paragraphs';
import { getCountries, getCurrencies } from 'state/reducers/global.reducers';
import { format, isValid } from 'date-fns';
import _ from 'lodash';
import { Fragment } from 'react';
import Footer from '../Footer';
import { getFile } from 'services/global.services';
import { getFormattedNumber } from 'utils';
import { getPaymentById } from 'state/reducers/requests.reducers';
import { usePaymentCategoryDisplay } from 'hooks/custom.hooks';
import { formatCategoryTreeForDisplay } from 'utils/paymentCategoryUtils';

export default function Step4(props: { _payment?: Pay; readOnly?: boolean }) {
	const t = useAppSelector(selectTranslations);
	const theme = useTheme();
	interface extraPay extends Pay {}
	const pay: extraPay = useAppSelector(getNewPay);
	let { id } = useParams();
	const existingPayment = useAppSelector(getPaymentById(id!)) || {};
	let payment = {
		...pay,
		[PayEnum.createdAt]: new Date(),
		[PayEnum.dueDate]: _.get(pay, PayEnum.paymentDate),
		...existingPayment,
		files: _.get(existingPayment, 'files', []),
	};
	payment = props._payment ? props._payment : payment;
	const beneficiary = useAppSelector(getBeneficiaryById(String(payment[PayEnum.beneficiaryId])));
	const Countries = _.keyBy(useAppSelector(getCountries), 'id');
	const Currencies = _.keyBy(useAppSelector(getCurrencies), 'id');
	const prefixFields = 'page.pay.create.step.4.';
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const purposeCodes = useAppSelector((state) => state.global.paymentPurpose);
	const language = localStorage.getItem('language');

	// Always call hooks (React rules)
	const hookCategoryDisplay = usePaymentCategoryDisplay(beneficiary, payment[PayEnum.paymentCategory]);

	// Format payment category from backend's categoryTree, or use hook fallback
	const paymentCategoryDisplay =
		payment.categoryTree && payment.categoryTree.length > 0
			? formatCategoryTreeForDisplay(payment.categoryTree, t)
			: hookCategoryDisplay;

	function formatValue(value?: any, key?: string) {
		let currency = Currencies[beneficiary?.currencyId || ''] || {};

		switch (key) {
			case PayEnum.amount:
				return currency?.symbol + getFormattedNumber(value);
			case PayEnum.fee:
				return `${currency?.symbol}${
					_.get(payment, 'fee.amount')
						? getFormattedNumber(_.get(payment, 'fee.amount'))
						: getFormattedNumber(value)
				}`;
			case PayEnum.iban:
				return beneficiary?.iban;
			case PayEnum.paymentDate:
			case PayEnum.createdAt:
			case PayEnum.dueDate:
			case PayEnum.settlementDate:
			case PayEnum.updatedAt:
				return value !== null && isValid(new Date(value)) ? format(new Date(value), timing.DATE) : value;
			case 'countryId':
			case 'bankAccountCountryId':
				return _.get(Countries[value || ''], 'name') || '';
			case 'currencyId':
				return _.get(Currencies[value || ''], 'name') || '';
			case PayEnum.paymentCategory:
				// This case shouldn't be used as we use usePaymentCategoryDisplay hook
				// Just return the ID as fallback
				return value;
			case PayEnum.status:
				return t('page.activity.table.status.' + _.snakeCase(value || '') + '.text');
			default:
				return value;
		}
	}
	const _reference = _.has(payment, 'complianceReference') ? 'complianceReference' : PayEnum.reference;
	return (
		<FormsStack>
			<InfoCard>
				<Stack direction={'row'} justifyContent={'space-between'}>
					<FormHeader1 title={t(`${prefixFields}beneficiary_details.title`)} />
					<FormButton autoheight="true" autowidth="true">
						<Button
							variant="outlined"
							onClick={() => navigate(`/${pagesEnum.BENEFICIARY}/details/${beneficiary?.id}`)}
						>
							{t(`${prefixFields}beneficiary_details.view_profile_button.text`)}
						</Button>
					</FormButton>
				</Stack>
				<BeneficiaryNameWithFlag beneficiary={beneficiary || {}} />
			</InfoCard>
			<InfoCard>
				<Stack direction={'row'} justifyContent={'space-between'} position={'relative'}>
					<FormHeader1 title={t(prefixFields + 'receipt.title')} />
					<Stack gap={1} position={'absolute'} right={0}>
						{_.isArray(_.get(payment, 'files')) &&
							_.has(payment, 'companyId') &&
							payment.files.map((file, index) => (
								<FormButton key={index} autoheight="true" autowidth="true">
									<Button
										variant="outlined"
										onClick={() => {
											dispatch(getFile(file.key || '', _.toNumber(_.get(payment, 'companyId'))));
										}}
									>
										{`${t(prefixFields + 'invoice_file.text')} ${index + 1}`}
									</Button>
								</FormButton>
							))}
					</Stack>
				</Stack>
				<Stack spacing={2}>
					<Paragraph3
						color={theme.palette.root.basicDeepBlue}
						title={t(prefixFields + _.snakeCase(PayEnum.createdAt) + '.text')}
						text={formatValue(_.get(payment, PayEnum.createdAt), PayEnum.createdAt)}
					/>
					<Paragraph3
						color={theme.palette.root.basicDeepBlue}
						title={t(prefixFields + _.snakeCase(PayEnum.dueDate) + '.text')}
						text={formatValue(_.get(payment, PayEnum.dueDate), PayEnum.dueDate)}
					/>
					{payment[PayEnum.settlementDate] && (
						<Paragraph3
							color={theme.palette.root.basicDeepBlue}
							title={t(prefixFields + _.snakeCase(PayEnum.settlementDate) + '.text')}
							text={formatValue(_.get(payment, PayEnum.settlementDate), PayEnum.settlementDate)}
						/>
					)}
					{payment[PayEnum.updatedAt] && (
						<Paragraph3
							color={theme.palette.root.basicDeepBlue}
							title={t(prefixFields + _.snakeCase(PayEnum.updatedAt) + '.text')}
							text={formatValue(_.get(payment, PayEnum.updatedAt), PayEnum.updatedAt)}
						/>
					)}
					{payment[PayEnum.status] && (
						<Paragraph3
							color={theme.palette.root.basicDeepBlue}
							title={t(prefixFields + _.snakeCase(PayEnum.status) + '.text')}
							text={formatValue(_.get(payment, PayEnum.status), PayEnum.status)}
						/>
					)}
					<Paragraph3
						color={theme.palette.root.basicDeepBlue}
						title={t(prefixFields + PayEnum.amount + '.text')}
						text={formatValue(payment[PayEnum.amount], PayEnum.amount)}
					/>
					<Paragraph3
						color={theme.palette.root.basicDeepBlue}
						title={t(prefixFields + PayEnum.fee + '.text')}
						text={formatValue(payment[PayEnum.fee], PayEnum.fee)}
					/>
					<Paragraph3
						color={theme.palette.root.basicDeepBlue}
						title={t(prefixFields + _.snakeCase(PayEnum.paymentType) + '.text')}
						text={t(prefixFields + _.snakeCase(PayEnum.paymentType) + '.' + payment.paymentType + '.text')}
					/>
					{_.isEqual(payment.paymentType, paymentsTypes.PRIORITY) && _.get(payment, 'swift') && (
						<Paragraph3
							color={theme.palette.root.basicDeepBlue}
							title={t(prefixFields + PayEnum.swift + '.text')}
							text={t(prefixFields + PayEnum.swift + '.' + payment.swift + '.text')}
						/>
					)}
					{paymentCategoryDisplay && (
						<Paragraph3
							color={theme.palette.root.basicDeepBlue}
							title={t('page.pay.create.step.4.payment_category.title')}
							text={paymentCategoryDisplay}
						/>
					)}
					{!paymentCategoryDisplay && payment[PayEnum.paymentCategory] && (
						<Paragraph3
							color={theme.palette.root.basicDeepBlue}
							title={t('page.pay.create.step.4.payment_category.title')}
							text={formatValue(payment[PayEnum.paymentCategory], PayEnum.paymentCategory)}
						/>
					)}
					{payment[PayEnum.comment] && (
						<Paragraph3
							color={theme.palette.root.basicDeepBlue}
							title={t(prefixFields + _.snakeCase(PayEnum.comment) + '.text')}
							text={payment[PayEnum.comment]}
						/>
					)}
					{payment[_reference] && (
						<Paragraph3
							color={theme.palette.root.basicDeepBlue}
							title={t(prefixFields + _.snakeCase(PayEnum.reference) + '.text')}
							text={payment[_reference]}
						/>
					)}
					{payment[PayEnum.invoiceNumber] && (
						<Paragraph3
							color={theme.palette.root.basicDeepBlue}
							title={t(prefixFields + _.snakeCase(PayEnum.invoiceNumber) + '.text')}
							text={String(payment[PayEnum.invoiceNumber])}
						/>
					)}
				</Stack>
			</InfoCard>
			<InfoCard>
				<FormHeader1 title={t(`${prefixFields}bank_account_details.title`)} />
				<Stack spacing={2}>
					{_.values(BeneficiaryDetailsBankAccount)
						.filter((field) => _.get(pay.beneficiary || '', field))
						.map((field) => {
							return (
								<Paragraph3
									key={field}
									color={theme.palette.root.basicDeepBlue}
									title={t(`${prefixFields}bank_account_details.${_.snakeCase(field)}.text`)}
									text={formatValue(_.get(beneficiary, field), field)}
								/>
							);
						})}
					{paymentsMethodsDisplayList.map((field) => {
						return !_.isNil(_.get(beneficiary, field)) ? (
							<Paragraph3
								key={field}
								//color={theme.palette.root.basicDeepBlue}
								title={t(`${prefixFields}bank_account_details.${_.snakeCase(field)}.text`)}
								text={formatValue(_.get(beneficiary, field), field)}
							/>
						) : (
							<Fragment key={field}></Fragment>
						);
					})}
				</Stack>
			</InfoCard>
			{!props.readOnly ? (
				<Footer
					handleCancel={() => {}}
					handleBack={() => {}}
					ifNoCancel
					ifBack={false}
					labelSubmit={t(`${prefixFields}done_button.text`)}
					handleSubmit={() => {
						dispatch(toggleUpserting(false));
						navigate(`/${pagesEnum.PAY}`);
					}}
				/>
			) : (
				<></>
			)}
		</FormsStack>
	);
}
